import { useApprovalStore } from '../stores/useApprovalStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

/** Human-readable one-liner for a pending tool call. */
function describeCall(name: string, input: Record<string, unknown>): string {
  const keys = Object.keys(input || {});
  if (keys.length === 0) return name;
  const preview = keys
    .slice(0, 3)
    .map((k) => `${k}: ${JSON.stringify(input[k])}`)
    .join(', ');
  return `${name} — ${preview}${keys.length > 3 ? ', …' : ''}`;
}

/**
 * App-level dialog for human-in-the-loop tool approval
 * (APPROVAL_MODE=selective|always). Driven by useApprovalStore, which the SSE
 * and Centrifugo transports feed on `approval_required` events. Approving or
 * rejecting unblocks the paused agent loop server-side.
 */
export default function ApprovalModal() {
  const { chatId, calls, submitting, respond } = useApprovalStore();
  const open = chatId !== null;

  return (
    // Controlled + non-dismissible: the only way out is Approve/Reject, so the
    // paused server loop always gets a decision. `open` stays bound to the
    // store and onOpenChange is a no-op, so Escape / outside-click requests to
    // close are ignored — they can't strand the loop.
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Approval needed</DialogTitle>
          <DialogDescription>
            {calls.length === 1
              ? 'The analysis wants to run the following action. Approve to continue.'
              : `The analysis wants to run ${calls.length} actions. Approve to continue.`}
          </DialogDescription>
        </DialogHeader>

        <ul className="my-2 max-h-64 space-y-2 overflow-y-auto">
          {calls.map((c) => (
            <li
              key={c.callId}
              className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs break-words"
            >
              {describeCall(c.name, c.input)}
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button
            variant="outline"
            disabled={submitting}
            onClick={() => respond(false)}
          >
            Reject
          </Button>
          <Button disabled={submitting} onClick={() => respond(true)}>
            {submitting ? 'Submitting…' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
